/**
 * „Upsell-Quote je Produkt und Bestellseite" (P1-V3 § 2.6, Umbau 25.10.2026): Produktzeilen mit ihren Bestellseiten als
 * Kinder, alle offen (Regel 14), Seiten ab 15 Anfos einzeln, der Rest als „weitere Seiten unter 15 Anfos" am Ende der
 * Gruppe. Dazu der Erwartete Wert je Anfo und ein Satz, wie viel eine Upsell-Anfo mehr wert ist — alle Zahlen live.
 * „Nur Seiten mit Wahl" (K5, geklärt 25.10.): ohne Seiten, die nur den Jahresbezug anbieten, und ohne Einmalkäufe.
 */
import { FC, useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import { BarDatum, ResponsiveBar } from '@nivo/bar';
import ChartCard from '../../../components/auswertungen/ChartCard';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { BefundZeile, nachGruppenReihenfolge, tEuro } from '../../../components/auswertungen/kennzahlTeile';
import { TipRow, useVizStyle, achse } from '../../../components/auswertungen/vizStyle';
import { formatEur, formatPct, spaltenTip, TERMS } from '../../../helpers/kpiDefinitions';
import { fetchUpsell, UpsellAntwort, UpsellSumme, UpsellZeile } from '../../../helpers/auswertungenData';
import { formatNumber } from '../../../helpers/numberUtils';
import { balkenHoehe, GeschaetztMarke, Hinweis, int, Laden, NamensEtikett, Strich, tagMonat, Unter, useLaden } from './shared';
import { AUSGELAUFEN_TAGE, ausgelaufenGrenze, produktLabel, upsellBefund, upsellStark } from './befunde';

/** Grau unter so vielen Anfos (Regel 3). */
const MIN_ANFOS = 20;
/** Persona-Umbau B7: eine Bestellseite mit weniger Upsells ist grau (die Quote hängt an Einzelfällen). */
const MIN_UPSELLS_SEITE = 5;

const GRUPPEN = ['Wert', 'Menge', 'Kundentyp', 'Zahlung (wächst mit dem Alter)', 'Planung'] as const;
const STANDARDS: Record<Breite, readonly string[]> = {
  breit: ['name', 'anfos', 'quote', 'wja', 'vonbis'],
  mittel: ['name', 'anfos', 'quote', 'wja'],
  schmal: ['name', 'quote'],
};

/** `ausgelaufen`: Bestellseite ohne Anfo in den letzten 30 Tagen — steht im Abschnitt „Ausgelaufen" am Ende (B7). */
type Zeile = UpsellZeile & { id: string; ebene: 0 | 1; ausgelaufen?: boolean };

/** Ausgelaufen: einzelne Seite (keine Restzeile), deren letzte Anfo vor `grenze` liegt. */
const istAusgelaufen = (x: UpsellZeile, grenze: string) => !x.rest && !!x.last_order && x.last_order < grenze;

/** Bestellseite lesbar: „alte Bestellseite 14/335 · cash-rallye-bestellen", „eigene Seite · Titel (27.09.)". */
export function seitenName(z: Pick<UpsellZeile, 'order_page' | 'webinar_title' | 'webinar_date' | 'rest'>, minAnfos: number): string {
  if (z.rest) return `weitere Seiten unter ${minAnfos} Anfos`;
  const s = z.order_page ?? '';
  const lpm = /^lpm\.[^/]+\/(\d+)\/(\d+)\/([^/]*)/.exec(s);
  if (lpm) return `alte Bestellseite ${lpm[1]}/${lpm[2]}${lpm[3] ? ` · ${lpm[3]}` : ''}`;
  if (/^lp\.finanztrends\.de\/w\//.test(s)) return `eigene Seite · ${z.webinar_title ?? s.split('/')[2]}${z.webinar_date ? ` (${tagMonat(z.webinar_date)})` : ''}`;
  return s || '(ohne Bestellseite)';
}

/**
 * Satz unter der Grafik, live aus der API (P1-V3 § 2.6, Regel 10). Nicht kausal. Der Euro-Hebel steht seit dem
 * Persona-Umbau B7 in der Befundzeile (`upsellBefund`), hier nur noch der Faktor und der Testvorbehalt.
 */
function upsellSatz(d: UpsellAntwort): string | null {
  const f = d.faktoren ?? [];
  const regel = d.faktor_regel ?? { min_faktor: 1.5, min_upsell_anfos: 20 };
  const stark = upsellStark(d);
  const kaum = f.filter((x) => x.faktor !== null && x.faktor < regel.min_faktor);
  if (!stark.length) return null;
  const fs = stark.map((x) => x.faktor as number);
  const zahl = (v: number) => formatNumber(v, 1);
  const spanne = Math.min(...fs) === Math.max(...fs) ? `${zahl(fs[0])}-mal` : `${zahl(Math.min(...fs))}- bis ${zahl(Math.max(...fs))}-mal`;
  return (
    `Bei ${stark.map((x) => x.plx_product).join(', ')} ist eine Upsell-Anfo im Schnitt ${spanne} so viel wert wie eine ohne.` +
    (kaum.length ? ` Bei ${kaum.map((x) => x.plx_product).join(' und ')} kaum mehr, dort ist schon eine Anfo ohne Upsell viel wert.` : '') +
    ' Ob ein anderes Angebot das bewirkt, zeigt nur ein Test, denn wer den Jahresbezug wählt, ist oft auch ein anderer Kunde.'
  );
}

const UpsellKarte: FC = () => {
  const { data, fehler } = useLaden(fetchUpsell);
  const viz = useVizStyle();
  const minSeite = data?.min_anfos_seite ?? 15;
  const s = data?.summary as UpsellSumme | undefined;
  const altGrenze = useMemo(() => (data ? ausgelaufenGrenze(data.window.asOf) : ''), [data]);

  // B7: Produkte oben (mit ihren laufenden Seiten als Kinder), ausgelaufene Seiten als eigene Zeilen im Abschnitt am Ende.
  const zeilen: Zeile[] = useMemo(
    () => [
      ...(data?.products ?? []).map((p) => ({ ...p, id: `p:${p.plx_product}`, ebene: 0 as const })),
      ...(data?.pages ?? []).filter((x) => istAusgelaufen(x, altGrenze)).map((x) => ({ ...x, id: `a:${x.plx_product}|${x.order_page}`, ebene: 1 as const, ausgelaufen: true })),
    ],
    [data, altGrenze],
  );
  const kinder = useMemo(() => {
    const m = new Map<string, Zeile[]>();
    for (const x of data?.pages ?? []) {
      if (istAusgelaufen(x, altGrenze)) continue;
      const liste = m.get(x.plx_product) ?? [];
      liste.push({ ...x, id: `s:${x.plx_product}|${x.rest ? '__rest' : x.order_page}`, ebene: 1 });
      m.set(x.plx_product, liste);
    }
    return m;
  }, [data, altGrenze]);

  const chartData = useMemo(
    () =>
      (data?.products ?? [])
        .filter((p) => p.anfos - (p.forced_anfos ?? 0) >= MIN_ANFOS && p.one_time_anfos < p.anfos)
        .map((p) => ({
          produkt: p.plx_product,
          name: p.name,
          quote: p.upsell_rate_choice ?? p.upsell_rate ?? 0,
          upsell: p.upsell_anfos - (p.forced_upsell ?? 0),
          anfos: p.anfos - (p.forced_anfos ?? 0),
        }))
        .sort((a, b) => a.quote - b.quote),
    [data],
  );

  // Restzeilen stehen immer am Ende ihrer Gruppe: jede Sortierung liefert für sie null.
  const sv = <T,>(f: (r: Zeile) => T | null) => (r: Zeile) => (r.rest ? null : f(r));
  const baue = (breit = true): Spalte<Zeile>[] => [
    {
      key: 'name',
      label: 'Produkt / Bestellseite',
      gruppe: 'Menge',
      pflicht: true,
      minWidth: breit ? 220 : undefined,
      render: (r) =>
        r.ebene === 0 ? (
          <Box sx={{ lineHeight: 1.3 }}>
            {/* Audit C3: Artikelnummer nicht vor den Namen („2010008 · Die 100% Gegenoffensive" → nur der Name). */}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {produktLabel(r.plx_product || '(ohne)', r.name)}
            </Box>
            {r.name !== r.plx_product && !/^\d/.test(r.plx_product) && ` · ${r.name}`}
          </Box>
        ) : (
          <Tooltip title={r.order_page ?? 'Seiten mit weniger Anfos zusammengefasst'} arrow placement="top">
            <Box component="span" sx={{ fontSize: 13, cursor: 'help', color: r.rest ? 'text.secondary' : 'inherit', overflowWrap: 'anywhere' }}>
              {r.ausgelaufen && <b>{r.plx_product} · </b>}
              {seitenName(r, minSeite)}
            </Box>
          </Tooltip>
        ),
      sortValue: sv((r) => (r.ebene === 0 ? r.plx_product : `${r.ausgelaufen ? `${r.plx_product} ` : ''}${seitenName(r, minSeite)}`)),
    },
    {
      key: 'anfos',
      label: TERMS.anfos.label,
      tip: spaltenTip('anfos', `Ohne vorläufig ergänzte.`),
      gruppe: 'Menge',
      pflicht: true,
      align: 'right',
      render: (r) => int(r.anfos),
      sortValue: sv((r) => r.anfos),
      summary: int(s?.anfos),
    },
    {
      key: 'quote',
      label: TERMS.upsellJeSeite.label,
      einheit: 'x von y',
      tip: TERMS.upsellJeSeite.tip,
      hinweis: `Grau: unter ${MIN_ANFOS} Anfos, bei einer Bestellseite auch unter ${MIN_UPSELLS_SEITE} Upsells.`,
      gruppe: 'Kundentyp',
      pflicht: true,
      align: 'right',
      // Ausgelaufene Seiten stehen ohnehin grau im eigenen Abschnitt — ein zweites Grau hätte ihn beim Sortieren in zwei
      // Läufe geteilt (Runde 2).
      unsicher: (r) =>
        r.only_annual || r.ausgelaufen
          ? null
          : r.anfos < MIN_ANFOS
            ? `unter ${MIN_ANFOS} Anfos`
            : r.ebene === 1 && !r.rest && r.upsell_anfos < MIN_UPSELLS_SEITE
              ? `unter ${MIN_UPSELLS_SEITE} Upsells`
              : null,
      render: (r) => {
        if (r.only_annual) return <Strich grund="Diese Seite bietet nur den Jahresbezug an — jede Anfo ist ein Upsell, die Quote ist kein Kundenverhalten." />;
        if (r.ebene === 0 && r.one_time_anfos >= r.anfos) return <Strich grund="Einmalkauf: ohne Jahresbezug." />;
        const mitWahl = r.ebene === 0 && (r.forced_anfos ?? 0) > 0;
        const q = mitWahl ? r.upsell_rate_choice ?? null : r.upsell_rate;
        const z = mitWahl ? r.upsell_anfos - (r.forced_upsell ?? 0) : r.upsell_anfos;
        const n = mitWahl ? r.anfos - (r.forced_anfos ?? 0) : r.anfos;
        return (
          <Box sx={{ lineHeight: 1.25 }}>
            {formatPct(q)}
            <Unter>
              {int(z)} von {int(n)}
              {mitWahl && ', nur Seiten mit Wahl'}
            </Unter>
          </Box>
        );
      },
      // Was als Strich dasteht (nur Jahresbezug, Einmalkauf), sortiert als leer ans Ende (Darstellungsprüfung Runde 2).
      sortValue: sv((r) =>
        r.only_annual || (r.ebene === 0 && r.one_time_anfos >= r.anfos)
          ? null
          : r.ebene === 0 && (r.forced_anfos ?? 0) > 0
            ? (r.upsell_rate_choice ?? null)
            : r.upsell_rate,
      ),
      summary: s ? (
        <Box sx={{ lineHeight: 1.25 }}>
          {formatPct(s.upsell_rate)}
          <Unter>
            {int(s.upsell_anfos)} von {int(s.anfos)} · nur Seiten mit Wahl {formatPct(s.upsell_rate_choice ?? null)}
          </Unter>
        </Box>
      ) : null,
    },
    {
      key: 'wja',
      label: 'Erwarteter Wert',
      einheit: 'je Anfo',
      tip: spaltenTip('wertBeiBestellung', `Mittel über die Anfos mit Wert, ohne vorläufige — die Upsell-Quote in Euro.`),
      gruppe: 'Wert',
      align: 'right',
      unsicher: (r) => ((r.with_value ?? 0) > 0 && (r.with_value ?? 0) < MIN_ANFOS ? `unter ${MIN_ANFOS} Anfos` : null),
      render: (r) => formatEur(r.value_per_anfo ?? null, 0),
      sortValue: sv((r) => r.value_per_anfo ?? null),
      summary: formatEur(s?.value_per_anfo ?? null, 0),
    },
    {
      key: 'vonbis',
      label: TERMS.bestellungenVonBis.label,
      einheit: TERMS.bestellungenVonBis.einheit,
      tip: TERMS.bestellungenVonBis.tip,
      gruppe: 'Planung',
      align: 'right',
      render: (r) => (r.ebene === 0 || r.rest || !r.first_order ? '' : `${tagMonat(r.first_order)}–${tagMonat(r.last_order)}${r.last_order.slice(2, 4)}`),
      sortValue: sv((r) => (r.ebene === 1 && r.last_order ? r.last_order : null)),
    },
    {
      key: 'summe',
      label: TERMS.wertSumme.label,
      tip: TERMS.wertSumme.tip,
      gruppe: 'Wert',
      align: 'right',
      render: (r) => tEuro(r.value_sum ?? null),
      sortValue: sv((r) => r.value_sum ?? null),
    },
    {
      key: 'pq',
      label: TERMS.bezahlquote.label,
      einheit: 'Anfos ab 60 Tagen',
      tip: spaltenTip('bezahlquote', `Unter 20 reifen Anfos grau.`),
      gruppe: 'Zahlung (wächst mit dem Alter)',
      align: 'right',
      unsicher: (r) => (r.pay_rate !== null && (r.mature_anfos ?? 0) < 20 ? 'wenig reife Anfos' : null),
      render: (r) =>
        r.pay_rate === null || r.pay_rate === undefined ? (
          '–'
        ) : (
          <Box sx={{ lineHeight: 1.25 }}>
            {formatPct(r.pay_rate)}
            <Unter>
              {int(r.mature_paid)} von {int(r.mature_anfos)}
            </Unter>
          </Box>
        ),
      sortValue: sv((r) => r.pay_rate ?? null),
    },
  ];
  const meta = nachGruppenReihenfolge(spaltenMeta(baue()), GRUPPEN);
  const wahl = useSpaltenwahl('upsell', meta, STANDARDS);
  const sichtbar = new Set(wahl.sichtbar);
  const spalten = baue(wahl.breite === 'breit').filter((c) => sichtbar.has(c.key));
  const satz = data ? upsellSatz(data) : null;
  const befund = data ? upsellBefund(data) : null;

  return (
    <ChartCard
      title="Upsell-Quote je Produkt und Bestellseite"
      actions={<SpaltenMenue spalten={meta} wahl={wahl} />}
      howToRead="Welcher Anteil der Anfos gleich den Jahresbezug (Upsell) gewählt hat, und was eine Anfo erwarten lässt. Die Grafik zeigt je Produkt die Quote dort, wo der Kunde wählen konnte. Die Tabelle zeigt je Produkt seine Bestellseiten; Seiten mit wenigen Anfos sind in einer Zeile am Ende zusammengefasst."
      terms={[
        { label: TERMS.upsellQuote.label, tip: TERMS.upsellQuote.tip },
        { label: TERMS.wertBeiBestellung.label, tip: TERMS.wertBeiBestellung.tip },
      ]}
      source="PL-X-Bestellungen, Werbeaktions-Konfiguration, Anfo-Wert je Anfo"
      freshnessKey={['plx_orders', 'anfo_werte']}
    >
      <Box ref={wahl.messRef}>
        {!data ? (
          <Laden fehler={fehler} />
        ) : (
          <>
            {/* B7: der Euro-Hebel als Befund (Schätzung, aus `faktoren` der API). */}
            <BefundZeile text={befund} fehlt={befund ? null : 'Bei keinem Produkt ist eine Upsell-Anfo deutlich mehr wert — kein Euro-Hebel.'} tip={TERMS.upsellQuote.tip} />
            <Box sx={{ height: balkenHoehe(chartData.length) }}>
              <ResponsiveBar
                data={chartData as unknown as BarDatum[]}
                keys={['quote']}
                indexBy="produkt"
                layout="horizontal"
                margin={{ top: 4, right: 64, bottom: 36, left: 72 }}
                padding={0.3}
                borderRadius={4}
                colors={[viz.series1]}
                theme={viz.nivo}
                animate={false}
                enableGridY={false}
                enableGridX
                gridXValues={5}
                enableLabel={false}
                axisLeft={{ tickSize: 0, tickPadding: 8 }}
                axisBottom={{ tickSize: 0, tickPadding: 6, tickValues: 5, format: achse('%') }}
                layers={[
                  'grid',
                  'axes',
                  'bars',
                  ({ bars }) => (
                    <g>
                      {bars.map((b) => (
                        <text key={b.key} x={b.x + b.width + 6} y={b.y + b.height / 2} dominantBaseline="central" fontSize={12} fontFamily={viz.fontFamily} fill={viz.inkSecondary}>
                          {formatPct(Number(b.data.value))}
                        </text>
                      ))}
                    </g>
                  ),
                ]}
                tooltip={({ data: d }) => (
                  <Box>
                    <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                      {String(d.produkt)} · {String(d.name)}
                    </Box>
                    <TipRow color={viz.series1} label="Upsell-Quote, Seiten mit Wahl" value={formatPct(Number(d.quote))} />
                    <TipRow label={`Upsell von ${int(Number(d.anfos))} Anfos`} value={int(Number(d.upsell))} />
                  </Box>
                )}
                role="img"
                ariaLabel="Upsell-Quote je Produkt, letzte 12 Monate, nur Seiten mit Wahl"
              />
            </Box>
            {satz && <Box sx={{ fontSize: 14, mt: 1, mb: 1.5, maxWidth: '75ch', lineHeight: 1.5 }}>{satz}</Box>}
            <SortableTable
              rows={zeilen}
              columns={spalten}
              rowKey={(r) => r.id}
              initialSort={{ key: 'anfos', dir: 'desc' }}
              maxHeight={null}
              eng={wahl.breite !== 'breit'}
              kinder={(r) => (r.ebene === 0 ? kinder.get(r.plx_product) : undefined)}
              initialOffen={zeilen.filter((r) => r.ebene === 0).map((r) => r.id)}
              abschnitt={(r) => (r.ausgelaufen ? `${TERMS.bestellseiteAusgelaufen.label} — ohne Anfo in den letzten ${AUSGELAUFEN_TAGE} Tagen` : null)}
              muted={(r) => !!r.ausgelaufen}
              namensEtikett={(r) => (
                <>
                  {r.ausgelaufen && r.last_order && <NamensEtikett tip={TERMS.bestellseiteAusgelaufen.tip}>{`letzte Anfo ${tagMonat(r.last_order)}`}</NamensEtikett>}
                  {r.ebene === 0 && <GeschaetztMarke anfos={r.wert_geschaetzt_anfos} />}
                </>
              )}
              summaryLabel="Alle Anfos"
            />
            <Hinweis>
              Upsell, nur Seiten mit Wahl: {formatPct(s?.upsell_rate_choice ?? null)} ({int(s?.upsell_choice)} von {int(s?.anfos_choice)}) · alle
              Seiten: {formatPct(s?.upsell_rate ?? null)} ({int(s?.upsell_anfos)} von {int(s?.anfos)}, ohne vorläufige). „Nur Seiten mit Wahl“ lässt
              Seiten aus, die nur den Jahresbezug anbieten, und Einmalkäufe (ohne Jahresbezug). Bestellseiten einzeln ab {minSeite} Anfos; ohne Anfo in den letzten{' '}
              {AUSGELAUFEN_TAGE} Tagen stehen sie im Abschnitt „{TERMS.bestellseiteAusgelaufen.label}“ (ihre Anfos zählen weiter beim Produkt). Grau: unter {MIN_ANFOS}{' '}
              Anfos bzw. unter {MIN_UPSELLS_SEITE} Upsells je Seite. Geteilt wird durch alle Anfos: in den PL-X-Daten ersetzt die Upsell-Bestellung die
              Erstbestellung.
            </Hinweis>
          </>
        )}
      </Box>
    </ChartCard>
  );
};

export default UpsellKarte;
